import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
/* actions */
import {
  getAllDepartments,
  editDepartments
} from '../../redux/actions/departments';
import { getAllDevices } from '../../redux/actions/devices';
import { getAllUsers } from '../../redux/actions/users';
import { getAllCompanies } from '../../redux/actions/companies';
import ErrorMsgField from '../../components/ErrorMsgField';
/* components */
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
/* form-validation */
import schema from '../../form-validation/department';
/* utils */
import sortable from '../../utils/sortable';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import handleChangeDefault from '../../utils/handleChangeDefault';
import { idCompany4Case } from '../../utils/defaultPermissions';
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    name: '',
    macroDepartment: '',
    description: '',
    extensions: [],
    groups: [],
    users: [],
    company: ''
  };
  const { register, handleSubmit, errors, setError, setValue } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const departments = useSelector(state => state.departments);
  const devices = useSelector(state => state.devices.data);
  const usersData = useSelector(state => state.users.data);
  const department = departments.data.find(dpt => dpt._id === id);
  const dispatch = useDispatch();
  const [selectedOptions, setSelectedOptions] = useState({
    extensions: [],
    groups: [],
    users: []
  });
  const [extensions, setExtensions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(INITIAL_VALUES);
  const submitButton = useRef();
  const companies = useSelector(state => state.companies.data);
  const [companyOptions, setCompanyOptions] = useState([firstOption]);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllDevices());
    dispatch(getAllUsers({ company }));
    dispatch(
      getAllCompanies(idCompany4Case === company ? {} : { _id: company })
    );
  }, [dispatch]);

  useEffect(() => {
    if (companies.length) {
      const options = companies
        .map(company => ({
          value: company._id,
          label: `${company.name}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setCompanyOptions([...options]);
    }
  }, [companies, setCompanyOptions]);

  useEffect(() => {
    if (department) {
      const newData = {
        ...INITIAL_VALUES,
        ...department,
        macroDepartment: department.macroDepartment._id || '',
        company: department.company._id || ''
      };
      setData(newData);
    }
  }, [department]);

  useEffect(() => {
    if (devices.length) {
      const extensions = devices
        .filter(device => device.type === 'E' && device.department._id !== id)
        .map(device => ({
          sortValue: `${device.number}`,
          value: device._id,
          text: `${device.number} - Dpto: ${device.department.name}`
        }))
        .sort(sortable);
      setExtensions([...extensions]);

      const groups = devices
        .filter(device => device.type === 'G' && device.department._id !== id)
        .map(device => ({
          sortValue: `${device.number}`,
          value: device._id,
          text: `${device.number} | ${device.name} - Dpto: ${device.department.name}`
        }))
        .sort(sortable);
      setGroups([...groups]);
    }
  }, [devices, setExtensions]);

  useEffect(() => {
    if (usersData.length) {
      const idRoot = '5ca65c51e0100957f32d1776';
      const idAdministrator = '5ca65c51e0100957f32d1777';
      const removeUsers = [idRoot, idAdministrator];
      const usrs = usersData
        .filter(
          user => user.department._id !== id && !removeUsers.includes(user._id)
        )
        .map(user => ({
          sortValue: `${user.name}`,
          value: user._id,
          text: `${user.name} - Dpto: ${user.department.name}`
        }))
        .sort(sortable);
      setUsers([...usrs]);
    }
  }, [usersData, setUsers]);

  const submit = () => {
    dispatch(editDepartments(data, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {departments.editSuccess && (
        <AlertMsg type="success" msg="Departamento atualizado com sucesso" />
      )}
      {departments.errorContent &&
        !departments.errorContent.message.includes('400') &&
        departments.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização do departamento"
        />
      )}
      {departments.errorContent &&
        departments.errorContent.message.includes('400') &&
        departments.editError && (
        <AlertMsg
          type="danger"
          msg="Um departamento com o nome informado já está cadastrado"
        />
      )}
      {!departments.editSuccess && !departments.editError && (
        <AlertMsg type="info" msg="Área atualização de departamentos" />
      )}
      <FormHeader title="Atualizar Departamento" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <label htmlFor="name">
            Nome<span className="field-required">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            name="name"
            autoComplete="off"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.name}
            ref={register}
          />
          {errors.name && errors.name.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="macroDepartment">
            Macro Departamento<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="macroDepartment"
            name="macroDepartment"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.macroDepartment}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {departments.data.map((dpt, i) => {
              const _id = dpt._id || Date.now() + '' + i;
              return (
                <option key={_id} value={_id}>
                  Dpto: {dpt.name} - Empresa: {dpt.company.name}
                </option>
              );
            })}
          </select>
          {errors.macroDepartment &&
            errors.macroDepartment.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <div className="form-group form-inline mb-0">
            <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="company">
              Empresa
              <span className="field-required">*</span>
            </label>
            <Select
              className="col p-0"
              name="company"
              ref={register}
              onChange={evt =>
                handleChangeSelect(
                  { ...evt, name: 'company' },
                  data,
                  setData,
                  setError,
                  setValue
                )
              }
              value={companyOptions.find(
                company => company.value === data.company
              )}
              options={companyOptions}
              isSearchable={true}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
            />
          </div>
          {errors.company && errors.company.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            value={data.description}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group mb-2">
          <label className="mb-1">Mudar Departamento</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={extensions}
              selectedOptions={selectedOptions.extensions}
              handleMultiDeselect={options =>
                handleMultiDeselect(
                  options,
                  'extensions',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              handleMultiSelect={options =>
                handleMultiSelect(
                  options,
                  'extensions',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              valueProp="value"
              textProp="text"
              title="Ramais"
              name="extensions"
              ref={register}
              error={!!errors.extensions}
            />
            <MultiSelect
              avaliableOptions={groups}
              selectedOptions={selectedOptions.groups}
              handleMultiDeselect={options =>
                handleMultiDeselect(
                  options,
                  'groups',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              handleMultiSelect={options =>
                handleMultiSelect(
                  options,
                  'groups',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              valueProp="value"
              textProp="text"
              title="Grupos"
              name="groups"
              ref={register}
              error={!!errors.groups}
            />
            <MultiSelect
              avaliableOptions={users}
              selectedOptions={selectedOptions.users}
              handleMultiDeselect={options =>
                handleMultiDeselect(
                  options,
                  'users',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              handleMultiSelect={options =>
                handleMultiSelect(
                  options,
                  'users',
                  selectedOptions,
                  setSelectedOptions,
                  data,
                  setData
                )
              }
              valueProp="value"
              textProp="text"
              title="Usuários"
              name="users"
              ref={register}
              error={!!errors.users}
            />
          </div>
        </div>
        <FormFooter url="/departments" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
